import React from 'react';
import { Modal, Table, Tag, Alert, Typography, Card } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { CustomerQuote } from '../types.ts';

const { Text } = Typography;

interface AvailabilityCheckModalProps {
  quote: CustomerQuote | null;
  onClose: () => void;
}

const AvailabilityCheckModal: React.FC<AvailabilityCheckModalProps> = ({
  quote,
  onClose
}) => {
  if (!quote || !quote.availability) return null;

  const { availability_check } = quote.availability;

  const getStatusIcon = (item: any) => {
    if (item.error) {
      return <WarningOutlined style={{ color: '#faad14' }} />;
    }
    if (item.can_fulfill_now) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (item.can_fulfill_future) {
      return <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />;
    }
    return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const getStatusText = (item: any) => {
    if (item.error) {
      return <Tag color="warning">Errore Controllo</Tag>;
    }
    if (item.can_fulfill_now) {
      return <Tag color="success">Disponibile</Tag>;
    }
    if (item.can_fulfill_future) {
      return <Tag color="warning">Disponibile Futuro</Tag>;
    }
    return <Tag color="error">Non Disponibile</Tag>;
  };

  const columns = [
    {
      title: 'Stato',
      key: 'status',
      width: 60,
      render: (record: any) => getStatusIcon(record),
    },
    {
      title: 'Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (code: string, record: any) => (
        <div>
          <strong>{code}</strong>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.product_description}
          </div>
        </div>
      ),
    },
    {
      title: 'Richiesto',
      dataIndex: 'requested_quantity',
      key: 'requested_quantity',
      align: 'center' as const,
      render: (qty: number) => <Text strong>{qty}</Text>,
    },
    {
      title: 'Disponibile',
      dataIndex: 'available_quantity',
      key: 'available_quantity',
      align: 'center' as const,
      render: (qty: number, record: any) => (
        <div>
          <div>{qty}</div>
          {record.in_production_quantity > 0 && (
            <div style={{ fontSize: '11px', color: '#1890ff' }}>
              +{record.in_production_quantity} in produzione
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Mancante',
      key: 'shortage',
      align: 'center' as const,
      render: (record: any) => (
        <div>
          {record.shortage > 0 && (
            <Text type="danger">{record.shortage}</Text>
          )}
          {record.shortage === 0 && (
            <Text type="success">-</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Stato',
      key: 'availability_status',
      render: (record: any) => getStatusText(record),
    },
  ];

  const fullyAvailable = quote.availability.fully_available_now;
  const futureAvailable = quote.availability.fully_available_future;

  return (
    <Modal
      title={`Controllo Disponibilità - ${quote.quote_number}`}
      open={true}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <div style={{ marginBottom: 16 }}>
        {fullyAvailable ? (
          <Alert
            message="Tutti i prodotti sono disponibili"
            description="Il preventivo può essere processato immediatamente"
            type="success"
            icon={<CheckCircleOutlined />}
          />
        ) : futureAvailable ? (
          <Alert
            message="Disponibilità parziale"
            description="Alcuni prodotti sono in produzione ma saranno disponibili in futuro"
            type="warning"
            icon={<ExclamationCircleOutlined />}
          />
        ) : (
          <Alert
            message="Disponibilità insufficiente"
            description="Alcuni prodotti non hanno disponibilità sufficiente"
            type="error"
            icon={<WarningOutlined />}
          />
        )}
      </div>

      <Table
        dataSource={availability_check}
        columns={columns}
        pagination={false}
        rowKey="product_id"
        size="small"
      />

      <Card size="small" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Text>Prodotti Totali: <strong>{availability_check.length}</strong></Text>
          </div>
          <div>
            <Text>Disponibili Ora: <strong style={{ color: '#52c41a' }}>{availability_check.filter(item => item.can_fulfill_now).length}</strong></Text>
          </div>
          <div>
            <Text>Disponibili Futuro: <strong style={{ color: '#fa8c16' }}>{availability_check.filter(item => item.can_fulfill_future).length}</strong></Text>
          </div>
        </div>
      </Card>
    </Modal>
  );
};

export default AvailabilityCheckModal;
